import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  workOrdersApi,
  studiesApi,
  studyAnalytesApi,
  resultsApi,
  reportsApi,
  branchesApi,
  analyteFormulasApi,
  catalogApi,
} from "@/lib/api";
import { toast } from "sonner";
import { 
  FileEdit, 
  Search, 
  CheckCircle, 
  Download, 
  Save, 
  AlertCircle,
  CalendarIcon,
  Building2,
  XCircle,
  Printer,
  Eye,
  ArrowUp,
  ArrowDown,
  Check,
  Type,
  FileText,
  History,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { format, differenceInDays, differenceInMonths, differenceInYears } from "date-fns";
import { es } from "date-fns/locale";
import RichTextEditor from "@/components/RichTextEditor";
import MobilePDFViewer from "@/components/MobilePDFViewer";

// Helper function to convert age to days for comparison
// For max limits in years, we add extra days to ensure boundary cases are included
const convertAgeToDays = (value, unit, isMaxLimit = false) => {
  switch (unit) {
    case 'days': return value;
    case 'months': return value * 30 + (isMaxLimit ? 15 : 0);
    case 'years': {
      const baseDays = Math.floor(value * 365.25);
      return isMaxLimit ? baseDays + 364 : baseDays;
    }
    default: return value;
  }
};

// Helper function to get patient age in days from birth_date
const getPatientAgeInDays = (birthDate) => {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  return differenceInDays(today, birth);
};

// Helper function to get patient age in years from birth_date
const getPatientAgeInYears = (birthDate) => {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  return differenceInYears(today, birth);
};

// Helper function to get the appropriate reference based on patient age and sex
// Helper function to format number with 2 decimals
const formatDecimal = (value) => {
  if (value == null || value === '') return value;
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toFixed(2);
};

const getPatientReference = (analyte, patientBirthDate, patientSex) => {
  // Default reference
  const defaultRef = analyte.reference_text ||
    (analyte.reference_min != null && analyte.reference_max != null
      ? `${formatDecimal(analyte.reference_min)} - ${formatDecimal(analyte.reference_max)}`
      : "-");
  
  // If no age_sex_references or empty, return default
  if (!analyte.age_sex_references || analyte.age_sex_references.length === 0) {
    return defaultRef;
  }

  // Calculate patient age in days
  const patientAgeInDays = getPatientAgeInDays(patientBirthDate);
  if (patientAgeInDays === null) return defaultRef;

  // Normalize patient sex (M/F)
  const sexUpper = patientSex?.toUpperCase() || '';
  const normalizedSex = (sexUpper === 'MASCULINO' || sexUpper === 'M' || sexUpper === 'MALE') ? 'M' : 
                        (sexUpper === 'FEMENINO' || sexUpper === 'F' || sexUpper === 'FEMALE') ? 'F' : null;

  // Find matching reference - convert each limit to days using its own unit
  for (const ref of analyte.age_sex_references) {
    // Convert min age to days using min unit
    const minDays = convertAgeToDays(ref.age_min_value || 0, ref.age_min_unit || 'days', false);
    // Convert max age to days using max unit (with buffer for boundary cases)
    const maxDays = convertAgeToDays(ref.age_max_value || 0, ref.age_max_unit || 'days', true);
    
    // Check age range (patient age in days vs limits in days)
    if (patientAgeInDays >= minDays && patientAgeInDays <= maxDays) {
      // Check sex (both matches any, or specific sex matches)
      if (ref.sex === 'both' || ref.sex === normalizedSex) {
        // Return this reference
        if (ref.reference_text) return ref.reference_text;
        if (ref.reference_min != null && ref.reference_max != null) {
          return `${formatDecimal(ref.reference_min)} - ${formatDecimal(ref.reference_max)}`;
        }
        // Handle cases where only one limit is defined
        if (ref.reference_min != null && ref.reference_max == null) {
          return `Mayor a ${formatDecimal(ref.reference_min)}`;
        }
        if (ref.reference_min == null && ref.reference_max != null) {
          return `Hasta ${formatDecimal(ref.reference_max)}`;
        }
      }
    }
  }

  // No matching reference found, return default
  return defaultRef;
};

// Helper function to check result status against reference range
const getResultStatus = (value, reference) => {
  if (!value || !reference) return "normal";
  
  // Replace comma with dot for proper number parsing
  const cleanValue = String(value).replace(',', '.');
  const numValue = parseFloat(cleanValue);
  if (isNaN(numValue)) return "normal";
  
  // Clean reference string - replace commas with dots
  const cleanRef = String(reference).replace(/,/g, '.');
  
  // Try to parse reference range (format: "min - max" or "min-max")
  const rangeMatch = cleanRef.match(/(\d+\.?\d*)\s*[-–]\s*(\d+\.?\d*)/);
  if (rangeMatch) {
    const min = parseFloat(rangeMatch[1]);
    const max = parseFloat(rangeMatch[2]);
    
    if (numValue < min) return "low";
    if (numValue > max) return "high";
    return "normal";
  }
  
  return "normal";
};

// Helper function to format reference text - replaces <> with line breaks
const formatReferenceDisplay = (reference) => {
  if (!reference || reference === "-") return reference;
  
  // Check if contains <> separator
  if (reference.includes('<>')) {
    const parts = reference.split('<>').map(part => part.trim());
    return (
      <div className="text-left">
        {parts.map((part, idx) => (
          <div key={idx}>{part}</div>
        ))}
      </div>
    );
  }
  
  return reference;
};

// Status indicator component
const ResultStatusIcon = ({ status }) => {
  if (status === "low") {
    return <ArrowDown className="h-4 w-4 text-orange-500" />;
  }
  if (status === "high") {
    return <ArrowUp className="h-4 w-4 text-red-500" />;
  }
  return <Check className="h-4 w-4 text-green-500" />;
};

// Helper function to find next visible input (skipping subtitles)
const focusNextInput = (studyId, currentIndex, studyAnalytes, isNewAnalyte = false) => {
  let nextIndex = currentIndex + 1;
  const dataAttr = isNewAnalyte ? 'data-new-analyte-index' : 'data-analyte-index';
  
  // Find the next analyte that is not a subtitle
  while (nextIndex < studyAnalytes.length) {
    const nextAnalyte = studyAnalytes[nextIndex];
    if (nextAnalyte && nextAnalyte.result_type !== 'subtitle') {
      // Found a non-subtitle analyte, try to focus its input or select trigger
      // First try input
      let nextElement = document.querySelector(
        `input[data-study="${studyId}"][${dataAttr}="${nextIndex}"]`
      );
      // If no input, try select trigger (button)
      if (!nextElement) {
        nextElement = document.querySelector(
          `button[data-study="${studyId}"][${dataAttr}="${nextIndex}"]`
        );
      }
      if (nextElement) {
        nextElement.focus();
        return true;
      }
    }
    nextIndex++;
  }
  return false;
};

export default function ResultsCapture() {
  const [searchParams] = useSearchParams();
  const refreshKey = searchParams.get('_r'); // Parámetro de refresh
  
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [studies, setStudies] = useState([]);
  const [analytes, setAnalytes] = useState({});
  const [results, setResults] = useState({});
  const [existingResults, setExistingResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingResults, setEditingResults] = useState({}); // Track which results are being edited
  const [formulas, setFormulas] = useState([]); // Analyte calculation formulas
  // Analitos calculados borrados manualmente por estudio: { [studyId]: { [analyteId]: true } }
  const [deletedCalc, setDeletedCalc] = useState({});
  const [studyNotes, setStudyNotes] = useState({}); // Notes/observations for each study
  const [areasMap, setAreasMap] = useState({}); // area_id -> area_name
  const [studyAreaMap, setStudyAreaMap] = useState({}); // study_id -> area_id
  
  // Filters
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [branches, setBranches] = useState([]);
  const [startCalendarOpen, setStartCalendarOpen] = useState(false);
  const [endCalendarOpen, setEndCalendarOpen] = useState(false);
  
  // PDF Preview states
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  
  // Ref para el contenedor de estudios (scroll al cambiar de paciente)
  const studiesContainerRef = useRef(null);
  
  // Analyte History states
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedAnalyteName, setSelectedAnalyteName] = useState("");

  // Primera carga y refrescos por navegación
  useEffect(() => {
    fetchBranches();
    fetchFormulas();
    fetchAreaMaps();
    fetchOrders(true); // Primera carga con indicador
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]); // Incluir refreshKey para recargar al navegar

  // Cambio de filtros - sin indicador de carga
  useEffect(() => {
    fetchOrders(false);
  }, [startDate, endDate, selectedBranch, selectedStatus]);

  const fetchBranches = async () => {
    try {
      const response = await branchesApi.getAll();
      setBranches(response.data);
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  // Orden definido de áreas
  const AREA_ORDER = [
    "HEMATOLOGIA",
    "BIOQUIMICA CLINICA",
    "BIOQUÍMICA CLÍNICA",
    "INMUNOLOGIA ESPECIAL",
    "INMUNOLOGIA",
    "UROANALISIS",
    "MICROBIOLOGIA",
    "PARASITOLOGIA"
  ];

  const fetchAreaMaps = async () => {
    try {
      const [areasRes, studiesRes] = await Promise.all([
        catalogApi.areas.getAll(),
        studiesApi.getAll()
      ]);
      const aMap = {};
      for (const area of areasRes.data) {
        aMap[area.id] = area.name;
      }
      setAreasMap(aMap);
      
      const sMap = {};
      for (const study of studiesRes.data) {
        if (study.area_id) {
          sMap[study.id] = study.area_id;
        }
      }
      setStudyAreaMap(sMap);
    } catch (error) {
      console.error("Error loading area maps:", error);
    }
  };

  const getAreaSortKey = (studyId) => {
    const areaId = studyAreaMap[studyId];
    const areaName = areasMap[areaId] || "";
    const nameUpper = areaName.toUpperCase().trim();
    for (let i = 0; i < AREA_ORDER.length; i++) {
      if (AREA_ORDER[i].includes(nameUpper) || nameUpper.includes(AREA_ORDER[i])) {
        return i;
      }
    }
    return AREA_ORDER.length;
  };

  const getSortedStudies = (studies) => {
    if (!studies || Object.keys(studyAreaMap).length === 0) return studies || [];
    return [...studies].sort((a, b) => getAreaSortKey(a.study_id) - getAreaSortKey(b.study_id));
  };

  const fetchFormulas = async () => {
    try {
      const response = await analyteFormulasApi.getAll();
      // Only load active formulas
      setFormulas(response.data.filter(f => f.active));
    } catch (error) {
      console.error("Error loading formulas:", error);
    }
  };

  // Function to fetch analyte history for a patient
  const fetchAnalyteHistory = async (analyteId, analyteName) => {
    console.log("fetchAnalyteHistory called:", { analyteId, analyteName, selectedOrder });
    
    if (!selectedOrder?.patient_id) {
      console.error("No patient_id in selectedOrder:", selectedOrder);
      toast.error("No se encontró información del paciente");
      return;
    }
    
    setHistoryLoading(true);
    setSelectedAnalyteName(analyteName);
    setHistoryData([]); // Clear previous data
    setShowHistoryModal(true);
    
    try {
      const response = await resultsApi.getHistory(selectedOrder.patient_id, analyteId);
      console.log("History response:", response.data);
      setHistoryData(response.data || []);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Error al cargar el historial");
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Function to extract analyte codes from a formula string
  const extractFormulaCodes = (formulaStr) => {
    // Match sequences that start with letters and may contain numbers (analyte codes like BH10, BH11)
    // But exclude standalone numbers
    const matches = formulaStr.match(/[A-Z]+[A-Z0-9]*/gi);
    return matches ? [...new Set(matches.map(m => m.toUpperCase()))] : [];
  };

  // Function to calculate formulas based on current results
  const calculateFormulas = (studyId, currentResults, studyAnalytesList, deletedMap = deletedCalc) => {
    if (!studyAnalytesList || studyAnalytesList.length === 0) {
      return currentResults;
    }
    
    const updatedResults = JSON.parse(JSON.stringify(currentResults)); // Deep clone
    
    // Get all analyte codes and their values for this study
    const analyteValues = {};
    const analyteByCode = {};
    
    for (const analyte of studyAnalytesList) {
      const resultData = currentResults[studyId]?.[analyte.id];
      const value = resultData?.value;
      
      if (analyte.code) {
        const codeUpper = analyte.code.toUpperCase();
        analyteByCode[codeUpper] = analyte;
        // Only include numeric values (skip document/text types)
        if (value !== undefined && value !== null && value !== '' && !value.toString().includes('<')) {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            analyteValues[codeUpper] = numValue;
          }
        }
      }
    }
    
    // Check each formula
    for (const formula of formulas) {
      if (!formula.formula || !formula.result_code) continue;
      
      // Extract all codes used in the formula
      const formulaCodes = extractFormulaCodes(formula.formula);
      
      // Check if all required codes have values
      const hasAllValues = formulaCodes.every(code => analyteValues[code] !== undefined);
      
      if (!hasAllValues) {
        continue;
      }
      
      // Find if the result analyte exists in this study
      const resultCode = formula.result_code.toUpperCase();
      const resultAnalyte = studyAnalytesList.find(a => a.code && a.code.toUpperCase() === resultCode);
      if (!resultAnalyte) {
        continue;
      }

      // Analito calculado borrado manualmente: no recalcular.
      if (deletedMap?.[studyId]?.[resultAnalyte.id]) {
        continue;
      }
      
      // Don't calculate if result code is used in the formula (circular dependency)
      if (formulaCodes.includes(resultCode)) {
        continue;
      }
      
      // Try to evaluate the formula
      let formulaStr = formula.formula.toUpperCase();
      
      // Replace all analyte codes with their values (sort by length descending to avoid partial replacements)
      const sortedCodes = Object.keys(analyteValues).sort((a, b) => b.length - a.length);
      for (const code of sortedCodes) {
        const value = analyteValues[code];
        // Use lookahead/lookbehind for proper word boundaries
        const regex = new RegExp(`(?<![A-Z0-9])${code}(?![A-Z0-9])`, 'g');
        formulaStr = formulaStr.replace(regex, value.toString());
      }
      
      // Check if there are still letters (unreplaced codes) in the formula
      if (/[A-Za-z]/.test(formulaStr)) {
        continue;
      }
      
      try {
        // Only allow safe characters
        const allowed = /^[\d\s\+\-\*\/\.\(\)]+$/;
        if (allowed.test(formulaStr)) {
          const result = eval(formulaStr);
          if (!isNaN(result) && isFinite(result)) {
            // Update the result for this analyte
            if (!updatedResults[studyId]) {
              updatedResults[studyId] = {};
            }
            updatedResults[studyId][resultAnalyte.id] = {
              ...updatedResults[studyId]?.[resultAnalyte.id],
              value: result.toFixed(2),
              calculated: true
            };
            
            // Update analyteValues so dependent formulas can use this result
            analyteValues[resultCode] = result;
          }
        }
      } catch (e) {
        console.error("Error calculating formula:", e);
      }
    }
    
    return updatedResults;
  };

  const fetchOrders = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const startDateStr = startDate ? format(startDate, "yyyy-MM-dd") : null;
      const endDateStr = endDate ? format(endDate, "yyyy-MM-dd") : null;
      const branchFilter = selectedBranch === "all" ? null : selectedBranch;
      
      const response = await workOrdersApi.getAll(null, null, branchFilter, startDateStr, endDateStr);
      // Ordenar por order_number ascendente (1, 2, 3, 4...)
      const sortedOrders = response.data
        .filter((o) => o.status !== "delivered")
        .sort((a, b) => a.order_number.localeCompare(b.order_number, undefined, { numeric: true }));
      setOrders(sortedOrders);
      setLoading(false);
    } catch (error) {
      toast.error("Error al cargar órdenes");
      setLoading(false);
    }
  };

  // Función para refrescar todos los filtros y órdenes
  const handleRefreshAll = () => {
    const today = new Date();
    setStartDate(today);
    setEndDate(today);
    setSelectedBranch("all");
    setSelectedStatus("all");
    setSearchTerm("");
    setSelectedOrder(null);
    setResults({});
    toast.success("Filtros reiniciados");
  };

  const handleSelectOrder = async (order) => {
    setSelectedOrder(order);
    setResults({});
    setStudyNotes({}); // Clear notes from previous order
    
    // Refrescar la lista de órdenes para obtener datos actualizados
    await fetchOrders();
    
    // Scroll al inicio del contenedor de estudios al cambiar de paciente
    if (studiesContainerRef.current) {
      studiesContainerRef.current.scrollTop = 0;
    }

    try {
      // Load existing results
      const resultsRes = await resultsApi.getAll(order.id);
      setExistingResults(resultsRes.data);

      // Load analytes for ALL studies in PARALLEL (much faster)
      const analytesMap = {};
      const initialResults = {};
      
      // Fetch all analytes concurrently
      const analytesPromises = order.studies.map(async (study) => {
        const analytesRes = await studyAnalytesApi.getAll(study.study_id);
        return { studyId: study.study_id, analytes: analytesRes.data };
      });
      
      const analytesResults = await Promise.all(analytesPromises);
      
      // Build the analytesMap from parallel results
      for (const { studyId, analytes: studyAnalytes } of analytesResults) {
        analytesMap[studyId] = studyAnalytes;
        
        // Check if this study already has results
        const existingResult = resultsRes.data.find(r => r.study_id === studyId);
        
        // Initialize results with defaults if no existing results
        if (!existingResult) {
          for (const analyte of studyAnalytes) {
            // Initialize document templates
            if (analyte.result_type === 'document' && analyte.document_template) {
              if (!initialResults[studyId]) {
                initialResults[studyId] = {};
              }
              initialResults[studyId][analyte.id] = {
                value: analyte.document_template,
              };
            }
            // Initialize text type with first option as default
            else if (analyte.result_type === 'text' && analyte.text_options) {
              const options = analyte.text_options.split(',').map(opt => opt.trim()).filter(Boolean);
              if (options.length > 0) {
                if (!initialResults[studyId]) {
                  initialResults[studyId] = {};
                }
                initialResults[studyId][analyte.id] = {
                  value: options[0], // First option as default
                };
              }
            }
          }
        } else {
          // Existing result found - fill defaults for analytes NOT in the result
          // This handles partial results from LIS (e.g., only GLUCOSA and PROTEINAS)
          const existingAnalyteIds = new Set(
            (existingResult.values || []).map(v => v.analyte_id)
          );
          
          for (const analyte of studyAnalytes) {
            if (!existingAnalyteIds.has(analyte.id)) {
              // This analyte is not in the saved result - set default
              if (analyte.result_type === 'text' && analyte.text_options) {
                const options = analyte.text_options.split(',').map(opt => opt.trim()).filter(Boolean);
                if (options.length > 0) {
                  if (!initialResults[studyId]) {
                    initialResults[studyId] = {};
                  }
                  initialResults[studyId][analyte.id] = {
                    value: options[0],
                  };
                }
              } else if (analyte.result_type === 'document' && analyte.document_template) {
                if (!initialResults[studyId]) {
                  initialResults[studyId] = {};
                }
                initialResults[studyId][analyte.id] = {
                  value: analyte.document_template,
                };
              }
            }
          }
        }
      }
      
      setAnalytes(analytesMap);
      if (Object.keys(initialResults).length > 0) {
        setResults(initialResults);
      }
    } catch (error) {
      console.error("Error loading order details:", error);
    }
  };

  const handleResultChange = (studyId, analyteId, field, value) => {
    let nextDeleted = deletedCalc;
    if (field === "value") {
      const analyte = analytes[studyId]?.find((a) => a.id === analyteId);
      if (analyte && isCalculatedAnalyte(analyte)) {
        // Borrar (vaciar) un analito calculado con el teclado = marcarlo borrado
        // para que NO se recalcule. Escribir un valor lo reactiva.
        const studyMap = { ...(deletedCalc[studyId] || {}) };
        if (!value) studyMap[analyteId] = true; else delete studyMap[analyteId];
        nextDeleted = { ...deletedCalc, [studyId]: studyMap };
        setDeletedCalc(nextDeleted);
      } else if (analyte && deletedCalc[studyId] && Object.keys(deletedCalc[studyId]).length > 0) {
        // Cambio en un analito BASE: reactivar los calculados borrados del estudio.
        nextDeleted = { ...deletedCalc, [studyId]: {} };
        setDeletedCalc(nextDeleted);
      }
    }
    setResults((prev) => {
      const newResults = {
        ...prev,
        [studyId]: {
          ...prev[studyId],
          [analyteId]: {
            ...prev[studyId]?.[analyteId],
            [field]: value,
          },
        },
      };
      
      // If changing a value, try to calculate formulas
      if (field === "value" && analytes[studyId]) {
        return calculateFormulas(studyId, newResults, analytes[studyId], nextDeleted);
      }
      
      return newResults;
    });
  };

  const handleNotesChange = (studyId, notes) => {
    setStudyNotes(prev => ({
      ...prev,
      [studyId]: notes
    }));
  };

  // Load existing notes when results are loaded
  useEffect(() => {
    if (existingResults && existingResults.length > 0) {
      const notesMap = {};
      existingResults.forEach(result => {
        if (result.notes) {
          notesMap[result.study_id] = result.notes;
        }
      });
      setStudyNotes(notesMap); // Replace entirely, don't merge with previous order's notes
    } else {
      setStudyNotes({}); // Clear notes when no results
    }
  }, [existingResults]);

  // Sembrar analitos calculados BORRADOS desde los resultados guardados.
  useEffect(() => {
    if (existingResults && existingResults.length > 0) {
      const map = {};
      existingResults.forEach(r => {
        const dm = {};
        (r.values || []).forEach(v => { if (v.deleted) dm[v.analyte_id] = true; });
        if (Object.keys(dm).length) map[r.study_id] = dm;
      });
      if (Object.keys(map).length) setDeletedCalc(prev => ({ ...prev, ...map }));
    }
  }, [existingResults]);

  const saveResults = async (studyId, studyName) => {
    if (!results[studyId] || Object.keys(results[studyId]).length === 0) {
      toast.error("No hay resultados para guardar");
      return;
    }

    setSaving(true);
    try {
      const values = Object.entries(results[studyId]).map(
        ([analyteId, data]) => {
          const analyte = analytes[studyId]?.find((a) => a.id === analyteId);
          // Get reference based on patient age and sex
          const reference = analyte 
            ? getPatientReference(analyte, selectedOrder?.patient_birth_date, selectedOrder?.patient_gender)
            : "";
          
          // Handle both object format {value: "..."} and direct value
          const valueContent = typeof data === 'object' ? (data.value || "") : (data || "");
          
          return {
            analyte_id: analyteId,
            analyte_name: analyte?.name || "",
            value: valueContent,
            unit: analyte?.unit || "",
            reference: reference,
            is_abnormal: typeof data === 'object' ? (data.is_abnormal || false) : false,
            deleted: !!(deletedCalc[studyId] && deletedCalc[studyId][analyteId]),
          };
        }
      );

      await resultsApi.create({
        work_order_id: selectedOrder.id,
        study_id: studyId,
        values,
        notes: studyNotes[studyId] || "",
        validated: false,
      });

      toast.success(`Resultados guardados para ${studyName}`);
      
      // Clear editing state for this study
      setEditingResults(prev => ({ ...prev, [studyId]: false }));
      setResults(prev => ({ ...prev, [studyId]: {} }));
      
      // Refresh existing results and orders list
      const resultsRes = await resultsApi.getAll(selectedOrder.id);
      setExistingResults(resultsRes.data);
      fetchOrders(); // Refresh orders to update status
    } catch (error) {
      console.error("Error saving results:", error);
      const errorMsg = error.response?.data?.detail || "Error al guardar resultados";
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setSaving(false);
    }
  };

  // Update existing result
  const updateResults = async (resultId, studyId, studyName) => {
    if (!editingResults[studyId] || Object.keys(editingResults[studyId]).length === 0) {
      toast.error("No hay cambios para guardar");
      return;
    }

    setSaving(true);
    try {
      const existingResult = getStudyResult(studyId);
      const studyAnalytesList = analytes[studyId] || [];
      
      // Merge existing values with edited values
      const values = existingResult.values.map((val) => {
        const editedValue = editingResults[studyId]?.[val.analyte_id];
        const analyte = studyAnalytesList.find(a => a.id === val.analyte_id);
        const reference = analyte?.reference_text || 
          (analyte?.reference_min && analyte?.reference_max 
            ? `${formatDecimal(analyte.reference_min)} - ${formatDecimal(analyte.reference_max)}` 
            : val.reference);
        
        const newValue = editedValue !== undefined ? editedValue : val.value;
        const status = getResultStatus(newValue, reference);
        
        return {
          ...val,
          value: newValue,
          is_abnormal: status !== "normal",
          deleted: !!(deletedCalc[studyId] && deletedCalc[studyId][val.analyte_id]),
        };
      });

      await resultsApi.update(resultId, {
        values,
        notes: studyNotes[studyId] !== undefined ? studyNotes[studyId] : (existingResult.notes || ""),
        validated: false, // Reset validation when editing
      });

      toast.success(`Resultados actualizados para ${studyName}`);
      
      // Clear editing state
      setEditingResults(prev => ({ ...prev, [studyId]: false }));
      
      // Refresh existing results and orders list
      const resultsRes = await resultsApi.getAll(selectedOrder.id);
      setExistingResults(resultsRes.data);
      fetchOrders();
    } catch (error) {
      toast.error("Error al actualizar resultados");
    } finally {
      setSaving(false);
    }
  };

  // Save pending results (add new analytes to existing result)
  const savePendingResults = async (resultId, studyId, studyName) => {
    if (!results[studyId] || Object.keys(results[studyId]).length === 0) {
      toast.error("No hay resultados pendientes para guardar");
      return;
    }

    setSaving(true);
    try {
      const existingResult = getStudyResult(studyId);
      const studyAnalytesList = analytes[studyId] || [];
      
      // Get existing values
      const existingValues = [...existingResult.values];
      
      // Add new pending values
      const newValues = Object.entries(results[studyId])
        .filter(([analyteId, data]) => {
          // Only include if not already in existing values and has a value
          const alreadyExists = existingValues.find(v => v.analyte_id === analyteId);
          const valueContent = typeof data === 'object' ? (data.value || "") : (data || "");
          return !alreadyExists && valueContent;
        })
        .map(([analyteId, data]) => {
          const analyte = studyAnalytesList.find((a) => a.id === analyteId);
          const reference = analyte 
            ? getPatientReference(analyte, selectedOrder?.patient_birth_date, selectedOrder?.patient_gender)
            : "";
          
          const valueContent = typeof data === 'object' ? (data.value || "") : (data || "");
          const status = getResultStatus(valueContent, reference);
          
          return {
            analyte_id: analyteId,
            analyte_name: analyte?.name || "",
            value: valueContent,
            unit: analyte?.unit || "",
            reference: reference,
            is_abnormal: status !== "normal",
          };
        });
      
      if (newValues.length === 0) {
        toast.error("No hay resultados nuevos para guardar");
        setSaving(false);
        return;
      }
      
      // Merge existing and new values
      const allValues = [...existingValues, ...newValues];

      await resultsApi.update(resultId, {
        values: allValues,
        notes: studyNotes[studyId] !== undefined ? studyNotes[studyId] : (existingResult.notes || ""),
        validated: false,
      });

      toast.success(`${newValues.length} resultado(s) agregado(s) para ${studyName}`);
      
      // Clear pending results for this study
      setResults(prev => ({ ...prev, [studyId]: {} }));
      
      // Refresh existing results and orders list
      const resultsRes = await resultsApi.getAll(selectedOrder.id);
      setExistingResults(resultsRes.data);
      fetchOrders();
    } catch (error) {
      console.error("Error saving pending results:", error);
      toast.error("Error al guardar resultados pendientes");
    } finally {
      setSaving(false);
    }
  };

  // Handle editing existing result value
  const handleEditResultChange = (studyId, analyteId, value) => {
    let nextDeleted = deletedCalc;
    const analyte = analytes[studyId]?.find((a) => a.id === analyteId);
    if (analyte && isCalculatedAnalyte(analyte)) {
      // Borrar (vaciar) un analito calculado con el teclado = marcarlo borrado
      // para que NO se recalcule. Escribir un valor lo reactiva.
      const studyMap = { ...(deletedCalc[studyId] || {}) };
      if (!value) studyMap[analyteId] = true; else delete studyMap[analyteId];
      nextDeleted = { ...deletedCalc, [studyId]: studyMap };
      setDeletedCalc(nextDeleted);
    } else if (analyte && deletedCalc[studyId] && Object.keys(deletedCalc[studyId]).length > 0) {
      // Cambio en un analito BASE: reactivar los calculados borrados de este
      // estudio para que se vuelvan a calcular con los nuevos valores.
      nextDeleted = { ...deletedCalc, [studyId]: {} };
      setDeletedCalc(nextDeleted);
    }
    setEditingResults(prev => {
      const newEditingResults = {
        ...prev,
        [studyId]: {
          ...(prev[studyId] || {}),
          [analyteId]: value,
        },
      };
      
      // Recalculate formulas for editing mode
      if (analytes[studyId]) {
        return calculateEditingFormulas(studyId, newEditingResults, analytes[studyId], nextDeleted);
      }
      
      return newEditingResults;
    });
  };
  
  // Function to calculate formulas when editing existing results
  const calculateEditingFormulas = (studyId, currentEditingResults, studyAnalytesList, deletedMap = deletedCalc) => {
    if (!studyAnalytesList || studyAnalytesList.length === 0 || !formulas || formulas.length === 0) {
      return currentEditingResults;
    }
    
    const updatedEditingResults = JSON.parse(JSON.stringify(currentEditingResults)); // Deep clone
    
    // Get existing result for this study
    const existingResult = existingResults.find(r => r.study_id === studyId);
    
    // Get all analyte codes and their values for this study
    const analyteValues = {};
    const analyteByCode = {};
    
    for (const analyte of studyAnalytesList) {
      // First check if there's an edited value
      let value = currentEditingResults[studyId]?.[analyte.id];
      
      // If no edited value, use existing result value
      if (value === undefined && existingResult) {
        const existingValue = existingResult.values?.find(v => v.analyte_id === analyte.id);
        if (existingValue) {
          value = existingValue.value;
        }
      }
      
      if (analyte.code) {
        const codeUpper = analyte.code.toUpperCase();
        analyteByCode[codeUpper] = analyte;
        // Only include numeric values (skip document/text types)
        if (value !== undefined && value !== null && value !== '' && !value.toString().includes('<')) {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            analyteValues[codeUpper] = numValue;
          }
        }
      }
    }
    
    // Check each formula
    for (const formula of formulas) {
      if (!formula.formula || !formula.result_code) continue;
      
      // Extract all codes used in the formula
      const formulaCodes = extractFormulaCodes(formula.formula);
      
      // Check if all required codes have values
      const hasAllValues = formulaCodes.every(code => analyteValues[code] !== undefined);
      
      if (!hasAllValues) {
        continue;
      }
      
      // Find if the result analyte exists in this study
      const resultCode = formula.result_code.toUpperCase();
      const resultAnalyte = studyAnalytesList.find(a => a.code && a.code.toUpperCase() === resultCode);
      if (!resultAnalyte) {
        continue;
      }

      // Analito calculado borrado manualmente: no recalcular.
      if (deletedMap?.[studyId]?.[resultAnalyte.id]) {
        continue;
      }
      
      // Don't calculate if result code is used in the formula (circular dependency)
      if (formulaCodes.includes(resultCode)) {
        continue;
      }
      
      // Try to evaluate the formula
      let formulaStr = formula.formula.toUpperCase();
      
      // Replace all analyte codes with their values (sort by length descending to avoid partial replacements)
      const sortedCodes = Object.keys(analyteValues).sort((a, b) => b.length - a.length);
      for (const code of sortedCodes) {
        const val = analyteValues[code];
        const regex = new RegExp(`(?<![A-Z0-9])${code}(?![A-Z0-9])`, 'g');
        formulaStr = formulaStr.replace(regex, val.toString());
      }
      
      // Check if there are still letters (unreplaced codes) in the formula
      if (/[A-Za-z]/.test(formulaStr)) {
        continue;
      }
      
      try {
        // Only allow safe characters
        const allowed = /^[\d\s\+\-\*\/\.\(\)]+$/;
        if (allowed.test(formulaStr)) {
          const result = eval(formulaStr);
          if (!isNaN(result) && isFinite(result)) {
            // Update the editing result for this analyte
            if (!updatedEditingResults[studyId]) {
              updatedEditingResults[studyId] = {};
            }
            updatedEditingResults[studyId][resultAnalyte.id] = result.toFixed(2);
            
            // Update analyteValues so dependent formulas can use this result
            analyteValues[resultCode] = result;
          }
        }
      } catch (e) {
        console.error("Error calculating formula in editing mode:", e);
      }
    }
    
    return updatedEditingResults;
  };

  // Check if a study is being edited
  const isEditing = (studyId) => {
    return editingResults[studyId] && typeof editingResults[studyId] === 'object' && Object.keys(editingResults[studyId]).length > 0;
  };

  // Start editing a result
  const startEditing = (studyId, existingResult) => {
    const initialValues = {};
    const delMap = {};
    existingResult.values.forEach(val => {
      initialValues[val.analyte_id] = val.value;
      if (val.deleted) delMap[val.analyte_id] = true;
    });
    setEditingResults(prev => ({ ...prev, [studyId]: initialValues }));
    setDeletedCalc(prev => ({ ...prev, [studyId]: delMap }));
  };

  const isCalculatedAnalyte = (analyte) => !!(analyte?.code) && formulas.some(f => (f.result_code || "").toUpperCase() === analyte.code.toUpperCase());

  const validateResult = async (resultId) => {
    try {
      await resultsApi.validate(resultId);
      toast.success("Resultado validado");
      const resultsRes = await resultsApi.getAll(selectedOrder.id);
      setExistingResults(resultsRes.data);
      fetchOrders(); // Refresh orders to update status
    } catch (error) {
      toast.error("Error al validar resultado");
    }
  };

  const invalidateResult = async (resultId) => {
    try {
      await resultsApi.invalidate(resultId);
      toast.success("Resultado invalidado");
      const resultsRes = await resultsApi.getAll(selectedOrder.id);
      setExistingResults(resultsRes.data);
      fetchOrders(); // Refresh orders to update status
    } catch (error) {
      toast.error("Error al invalidar resultado");
    }
  };

  const downloadPdf = async () => {
    try {
      const response = await reportsApi.downloadResultsPdf(selectedOrder.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      // Usar nombre del paciente para el archivo, sanitizando caracteres especiales
      const patientName = selectedOrder.patient_name 
        ? selectedOrder.patient_name.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]/g, '').replace(/\s+/g, '_')
        : selectedOrder.order_number;
      link.setAttribute("download", `resultados_${patientName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Error al descargar PDF");
    }
  };

  const previewPdf = async () => {
    try {
      const response = await reportsApi.downloadResultsPdf(selectedOrder.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
      setShowPdfPreview(true);
    } catch (error) {
      toast.error("Error al generar vista previa");
    }
  };

  const printResults = async () => {
    try {
      const response = await reportsApi.downloadResultsPdf(selectedOrder.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Open in new window and trigger print
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        // Fallback if popup is blocked
        toast.info("Por favor, descargue el PDF para imprimir");
        const link = document.createElement("a");
        link.href = url;
        // Usar nombre del paciente para el archivo
        const patientName = selectedOrder.patient_name 
          ? selectedOrder.patient_name.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ\s]/g, '').replace(/\s+/g, '_')
          : selectedOrder.order_number;
        link.setAttribute("download", `resultados_${patientName}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      toast.error("Error al imprimir");
    }
  };

  const filteredOrders = orders.filter((order) => {
    // Filtro por búsqueda
    const matchesSearch = 
      order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.patient_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtro por estado
    const matchesStatus = selectedStatus === "all" || order.status === selectedStatus;
    
    return matchesSearch && matchesStatus;
  });

  const getStudyResult = (studyId) => {
    return existingResults.find((r) => r.study_id === studyId);
  };

  return (
    <div className="space-y-6" data-testid="results-capture-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
          Captura de Resultados
        </h1>
        <p className="text-muted-foreground">
          Ingrese los resultados de los estudios
        </p>
      </div>

      {/* Filters Section */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Start Date Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                Fecha Inicio
              </Label>
              <Popover open={startCalendarOpen} onOpenChange={setStartCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-[160px] justify-start text-left font-normal"
                    data-testid="start-date-filter"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? (
                      format(startDate, "dd/MM/yyyy")
                    ) : (
                      <span>Seleccionar</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date);
                      setStartCalendarOpen(false);
                      // Si la fecha fin es menor que la fecha inicio, actualizarla
                      if (date && endDate && date > endDate) {
                        setEndDate(date);
                      }
                    }}
                    initialFocus
                    locale={es}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* End Date Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                Fecha Fin
              </Label>
              <Popover open={endCalendarOpen} onOpenChange={setEndCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-[160px] justify-start text-left font-normal"
                    data-testid="end-date-filter"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? (
                      format(endDate, "dd/MM/yyyy")
                    ) : (
                      <span>Seleccionar</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      setEndDate(date);
                      setEndCalendarOpen(false);
                    }}
                    disabled={(date) => startDate && date < startDate}
                    initialFocus
                    locale={es}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Branch Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Sucursal
              </Label>
              <Select
                value={selectedBranch}
                onValueChange={setSelectedBranch}
              >
                <SelectTrigger className="w-[180px]" data-testid="branch-filter">
                  <SelectValue placeholder="Todas las sucursales" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {branches.map((branch) => (
                    <SelectItem key={branch.name || branch} value={branch.name || branch}>
                      {branch.name || branch}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                Estado
              </Label>
              <div className="flex items-center gap-2">
                <Select
                  value={selectedStatus}
                  onValueChange={setSelectedStatus}
                >
                  <SelectTrigger className="w-[150px]" data-testid="status-filter">
                    <SelectValue placeholder="Todos los estados" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="in_progress">En Proceso</SelectItem>
                    <SelectItem value="completed">Completado</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleRefreshAll}
                  data-testid="refresh-filters-btn"
                  title="Refrescar filtros"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Results count */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground ml-auto">
              <Badge variant="secondary">
                {filteredOrders.length} orden(es)
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Orders List - Wider */}
        <Card className="xl:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Órdenes del Día</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar orden..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-orders"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 text-center text-muted-foreground">
                Cargando...
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                No hay órdenes para esta fecha
              </div>
            ) : (
              <div className="divide-y overflow-y-auto" style={{ maxHeight: "calc(100vh - 320px)", minHeight: "500px" }}>
                {/* Header row */}
                <div className="grid grid-cols-[95px_minmax(200px,1fr)_40px_40px_75px_50px] gap-2 px-3 py-2 bg-white dark:bg-gray-950 text-xs font-semibold text-muted-foreground sticky top-0 z-10 border-b">
                  <span>FOLIO</span>
                  <span>NOMBRE</span>
                  <span>SEXO</span>
                  <span>EDAD</span>
                  <span>FECHA</span>
                  <span>HORA</span>
                </div>
                {filteredOrders.map((order) => {
                  // Format gender initial
                  const genderInitial = order.patient_gender === "male" ? "M" : order.patient_gender === "female" ? "F" : "-";
                  
                  // Format date and time separately (24h format)
                  // SIEMPRE usar created_at de la orden, no patient_registered_at
                  let dateStr = "";
                  let timeStr = "";
                  if (order.created_at) {
                    const dt = new Date(order.created_at);
                    dateStr = dt.toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "2-digit" });
                    timeStr = dt.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false });
                  }

                  return (
                    <button
                      key={order.id}
                      onClick={() => handleSelectOrder(order)}
                      className={`w-full text-left hover:bg-muted/50 transition-colors ${
                        selectedOrder?.id === order.id ? "bg-primary/10 border-l-4 border-primary" : ""
                      }`}
                      data-testid={`order-item-${order.id}`}
                    >
                      {/* Data row aligned with header */}
                      <div className="grid grid-cols-[95px_minmax(200px,1fr)_40px_40px_75px_50px] gap-2 px-3 py-2 text-sm items-center">
                        <span className="font-mono font-bold text-primary text-sm">{order.order_number}</span>
                        <span className="font-medium uppercase text-sm" title={order.patient_name}>
                          {order.patient_name}
                        </span>
                        <span className="font-semibold text-center">{genderInitial}</span>
                        <span className="text-center">{order.patient_age || "-"}</span>
                        <span className="text-muted-foreground text-xs">{dateStr}</span>
                        <span className="text-muted-foreground text-xs">{timeStr}</span>
                      </div>
                      
                      {/* Status row */}
                      {(order.company_name || order.branch || order.status) && (
                        <div className="flex items-center gap-2 px-3 pb-2 text-xs">
                          {order.company_name && (
                            <Badge variant="outline" className="text-xs">
                              {order.company_name}
                            </Badge>
                          )}
                          {order.branch && (
                            <Badge variant="secondary" className="text-xs">
                              {typeof order.branch === 'object' ? order.branch.name : order.branch}
                            </Badge>
                          )}
                          <Badge
                            variant="outline"
                            className={`ml-auto text-xs ${
                              order.status === "in_progress" 
                                ? "bg-yellow-100 text-yellow-800 border-yellow-300" 
                                : order.status === "completed" 
                                ? "bg-green-100 text-green-800 border-green-300" 
                                : ""
                            }`}
                          >
                            {order.status === "pending"
                              ? "Pendiente"
                              : order.status === "in_progress"
                              ? "En Proceso"
                              : "Completado"}
                          </Badge>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Entry - Takes remaining space */}
        <Card className="xl:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileEdit className="h-5 w-5" />
              {selectedOrder
                ? `Orden ${selectedOrder.order_number}`
                : "Seleccione una orden"}
            </CardTitle>
            {selectedOrder && existingResults.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={previewPdf}
                  data-testid="preview-pdf-btn"
                >
                  <Eye className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Ver PDF</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadPdf}
                  data-testid="download-pdf-btn"
                >
                  <Download className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Descargar</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={printResults}
                  data-testid="print-btn"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  <span className="hidden sm:inline">Imprimir</span>
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {!selectedOrder ? (
              <div className="text-center text-muted-foreground py-12" style={{ minHeight: "calc(100vh - 380px)" }}>
                <FileEdit className="h-12 w-12 mx-auto mb-4 opacity-30" />
                Seleccione una orden para capturar resultados
              </div>
            ) : (
              <div 
                ref={studiesContainerRef}
                className="space-y-6 overflow-y-auto" 
                style={{ maxHeight: "calc(100vh - 350px)", minHeight: "400px" }}
              >
                {/* Patient Observations */}
                {selectedOrder.patient_observations && (
                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                      <span className="font-semibold">Observaciones del paciente:</span>{" "}
                      {selectedOrder.patient_observations}
                    </AlertDescription>
                  </Alert>
                )}

                {getSortedStudies(selectedOrder.studies).map((study) => {
                  const studyAnalytes = analytes[study.study_id] || [];
                  const existingResult = getStudyResult(study.study_id);

                  return (
                    <div
                      key={study.study_id}
                      className="border rounded-lg p-4 space-y-4"
                    >
                      <div className="flex justify-between items-center">
                        <h3 className="font-semibold">{study.study_name}</h3>
                        {existingResult ? (
                          <Badge
                            variant={existingResult.validated ? "default" : "secondary"}
                            className={existingResult.validated ? "bg-green-600" : ""}
                          >
                            {existingResult.validated ? (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Validado
                              </>
                            ) : (
                              "Por validar"
                            )}
                          </Badge>
                        ) : (
                          <Badge variant="outline">Sin capturar</Badge>
                        )}
                      </div>

                      {studyAnalytes.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          No hay analitos configurados para este estudio
                        </p>
                      ) : existingResult ? (
                        <>
                          {/* Show ALL analytes - both captured and pending */}
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-muted/50">
                                  <TableHead className="bg-muted/50 w-[180px] font-semibold text-center">Analito</TableHead>
                                  <TableHead className="text-center w-[220px] font-semibold">Resultado</TableHead>
                                  <TableHead className="text-center w-[100px] font-semibold">Unidad</TableHead>
                                  <TableHead className="text-center w-[150px] font-semibold">Referencia</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {studyAnalytes.map((analyte, idx) => {
                                  // Find if this analyte has an existing result
                                  const existingValue = existingResult.values.find(v => v.analyte_id === analyte.id);
                                  const resultType = analyte?.result_type || "referenced_value";
                                  // Get reference based on patient age and sex
                                  const reference = getPatientReference(analyte, selectedOrder?.patient_birth_date, selectedOrder?.patient_gender);
                                  const unit = analyte?.unit || "-";
                                
                                // If analyte has existing result, show it
                                if (existingValue) {
                                  const currentValue = isEditing(study.study_id) 
                                    ? editingResults[study.study_id]?.[existingValue.analyte_id] ?? existingValue.value
                                    : existingValue.value;
                                const status = getResultStatus(currentValue, reference);
                                
                                // Special rendering for document type
                                if (resultType === "document") {
                                  return (
                                    <TableRow key={analyte.id} className="bg-green-50/30">
                                      <TableCell colSpan={4} className="p-4">
                                        <div className="space-y-2">
                                          <Label 
                                            className="flex items-center gap-2 font-medium cursor-pointer hover:text-primary hover:underline"
                                            onClick={() => fetchAnalyteHistory(analyte.id, analyte.name)}
                                            title="Ver historial de resultados"
                                          >
                                            <FileText className="h-4 w-4" />
                                            {analyte.name}
                                          </Label>
                                          {isEditing(study.study_id) ? (
                                            <RichTextEditor
                                              content={editingResults[study.study_id]?.[analyte.id] ?? existingValue.value}
                                              onChange={(html) => handleEditResultChange(study.study_id, analyte.id, html)}
                                              placeholder="Editar documento..."
                                            />
                                          ) : (
                                            <div 
                                              className="prose prose-sm max-w-none border rounded-md p-3 bg-muted/30 cursor-pointer hover:bg-muted/50"
                                              onClick={() => !existingResult.validated && startEditing(study.study_id, existingResult)}
                                              dangerouslySetInnerHTML={{ __html: existingValue.value || "<p>Sin contenido</p>" }}
                                            />
                                          )}
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  );
                                }
                                
                                // Special rendering for text type with dropdown options
                                if (resultType === "text") {
                                  const textOptions = analyte.text_options 
                                    ? analyte.text_options.split(',').map(opt => opt.trim()).filter(Boolean)
                                    : [];
                                  const hasOptions = textOptions.length > 0;
                                  
                                  return (
                                    <TableRow key={analyte.id} className="bg-green-50/30">
                                      <TableCell 
                                        className="bg-green-50/30 font-medium border-r text-center cursor-pointer hover:text-primary hover:underline"
                                        onClick={() => fetchAnalyteHistory(analyte.id, analyte.name)}
                                        title="Ver historial de resultados"
                                      >
                                        {analyte.name}
                                      </TableCell>
                                      <TableCell className="text-center" style={{ width: '180px', minWidth: '180px' }}>
                                        <div className="flex items-center justify-center gap-2 w-full">
                                          {isEditing(study.study_id) ? (
                                            hasOptions ? (
                                              <Select
                                                value={editingResults[study.study_id]?.[analyte.id] ?? existingValue.value}
                                                onValueChange={(value) => handleEditResultChange(study.study_id, analyte.id, value)}
                                              >
                                                <SelectTrigger 
                                                  className="w-52 h-8 shrink-0"
                                                  data-study={study.study_id}
                                                  data-analyte-index={idx}
                                                >
                                                  <SelectValue placeholder="Seleccionar..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {textOptions.map((option, i) => (
                                                    <SelectItem key={i} value={option}>
                                                      {option}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            ) : (
                                              <Input
                                                value={editingResults[study.study_id]?.[analyte.id] ?? existingValue.value}
                                                onChange={(e) => handleEditResultChange(study.study_id, analyte.id, e.target.value)}
                                                className="h-8 w-52 text-center shrink-0"
                                                data-study={study.study_id}
                                                data-analyte-index={idx}
                                                onKeyDown={(e) => {
                                                  if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    focusNextInput(study.study_id, idx, studyAnalytes);
                                                  }
                                                }}
                                              />
                                            )
                                          ) : (
                                            <span
                                              className="font-semibold cursor-pointer hover:text-primary min-w-[80px] text-center"
                                              onClick={() => !existingResult.validated && startEditing(study.study_id, existingResult)}
                                              title={existingResult.validated ? "Resultado validado" : "Click para editar"}
                                            >
                                              {existingValue.value}
                                            </span>
                                          )}
                                          <div className="w-16 shrink-0 text-center">
                                            <Badge 
                                              variant="outline" 
                                              className="text-xs px-1.5 py-0 whitespace-nowrap bg-green-100 text-green-700 border-green-300"
                                            >
                                              ✓
                                            </Badge>
                                          </div>
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-center text-muted-foreground">{unit}</TableCell>
                                      <TableCell className="text-center text-muted-foreground">{formatReferenceDisplay(reference)}</TableCell>
                                    </TableRow>
                                  );
                                }
                                
                                return (
                                  <TableRow key={analyte.id} className="bg-green-50/30">
                                    <TableCell 
                                      className="bg-green-50/30 font-medium border-r text-center cursor-pointer hover:text-primary hover:underline"
                                      onClick={() => fetchAnalyteHistory(analyte.id, analyte.name)}
                                      title="Ver historial de resultados"
                                    >
                                      {analyte.name}
                                    </TableCell>
                                    <TableCell className="text-center" style={{ width: '180px', minWidth: '180px' }}>
                                      <div className="flex items-center justify-center gap-2 w-full">
                                        {isEditing(study.study_id) ? (
                                          <Input
                                            value={editingResults[study.study_id]?.[analyte.id] ?? existingValue.value}
                                            onChange={(e) => handleEditResultChange(study.study_id, analyte.id, e.target.value)}
                                            className="h-8 w-52 text-center shrink-0"
                                            data-study={study.study_id}
                                            data-analyte-index={idx}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                e.preventDefault();
                                                focusNextInput(study.study_id, idx, studyAnalytes);
                                              }
                                            }}
                                          />
                                        ) : (
                                          <span
                                            className={`font-semibold cursor-pointer hover:text-primary min-w-[80px] text-center ${
                                              status === "high" ? "text-red-600" : 
                                              status === "low" ? "text-orange-600" : ""
                                            }`}
                                            onClick={() => !existingResult.validated && startEditing(study.study_id, existingResult)}
                                            title={existingResult.validated ? "Resultado validado" : "Click para editar"}
                                          >
                                            {existingValue.value}
                                          </span>
                                        )}
                                        {/* Alerta al lado del resultado - ancho fijo */}
                                        <div className="w-16 shrink-0 text-center">
                                          {!(isCalculatedAnalyte(analyte) && deletedCalc[study.study_id]?.[analyte.id]) && (
                                          <Badge 
                                            variant="outline" 
                                            className={`text-xs px-1.5 py-0 whitespace-nowrap ${
                                              status === "high" ? "bg-red-100 text-red-700 border-red-300" : 
                                              status === "low" ? "bg-orange-100 text-orange-700 border-orange-300" : 
                                              "bg-green-100 text-green-700 border-green-300"
                                            }`}
                                          >
                                            {status === "high" ? "↑ Alto" : status === "low" ? "↓ Bajo" : "✓"}
                                          </Badge>
                                          )}
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-center text-muted-foreground">{unit}</TableCell>
                                    <TableCell className="text-center text-muted-foreground">{formatReferenceDisplay(reference)}</TableCell>
                                  </TableRow>
                                );
                                }
                                
                                // PENDING ANALYTE - not yet captured
                                // Hide subtitle types completely - they only show in PDF print
                                if (resultType === "subtitle") {
                                  return null;
                                }
                                
                                // Pending document type
                                if (resultType === "document") {
                                  const pendingValue = results[study.study_id]?.[analyte.id]?.value || analyte.document_template || "";
                                  return (
                                    <TableRow key={analyte.id} className="bg-amber-50/30">
                                      <TableCell colSpan={4} className="p-4">
                                        <div className="space-y-2">
                                          <Label 
                                            className="flex items-center gap-2 font-medium cursor-pointer hover:text-primary hover:underline"
                                            onClick={() => fetchAnalyteHistory(analyte.id, analyte.name)}
                                            title="Ver historial de resultados"
                                          >
                                            <FileText className="h-4 w-4" />
                                            {analyte.name}
                                            <Badge variant="outline" className="text-xs bg-amber-100 text-amber-700 border-amber-300">
                                              Pendiente
                                            </Badge>
                                          </Label>
                                          <RichTextEditor
                                            content={pendingValue}
                                            onChange={(html) => handleResultChange(study.study_id, analyte.id, "value", html)}
                                            placeholder="Escribe el resultado aquí..."
                                          />
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  );
                                }
                                
                                // Pending text type
                                if (resultType === "text") {
                                  const textOptions = analyte.text_options 
                                    ? analyte.text_options.split(',').map(opt => opt.trim()).filter(Boolean)
                                    : [];
                                  const hasOptions = textOptions.length > 0;
                                  const pendingValue = results[study.study_id]?.[analyte.id]?.value || "";
                                  
                                  return (
                                    <TableRow key={analyte.id} className="bg-amber-50/30">
                                      <TableCell 
                                        className="bg-amber-50/30 font-medium border-r text-center cursor-pointer hover:text-primary hover:underline"
                                        onClick={() => fetchAnalyteHistory(analyte.id, analyte.name)}
                                        title="Ver historial de resultados"
                                      >
                                        {analyte.name}
                                        <Badge variant="outline" className="ml-2 text-xs bg-amber-100 text-amber-700 border-amber-300">
                                          Pendiente
                                        </Badge>
                                      </TableCell>
                                      <TableCell className="text-center" style={{ width: '180px', minWidth: '180px' }}>
                                        <div className="flex items-center justify-center gap-2 w-full">
                                        {hasOptions ? (
                                          <Select
                                            value={pendingValue}
                                            onValueChange={(value) => handleResultChange(study.study_id, analyte.id, "value", value)}
                                          >
                                            <SelectTrigger 
                                              className="w-52 h-8 shrink-0"
                                              data-study={study.study_id}
                                              data-analyte-index={idx}
                                            >
                                              <SelectValue placeholder="Seleccionar..." />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {textOptions.map((option, i) => (
                                                <SelectItem key={i} value={option}>
                                                  {option}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        ) : (
                                          <Input
                                            placeholder="Resultado..."
                                            value={pendingValue}
                                            onChange={(e) => handleResultChange(study.study_id, analyte.id, "value", e.target.value)}
                                            className="h-8 w-52 text-center shrink-0"
                                            data-study={study.study_id}
                                            data-analyte-index={idx}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                e.preventDefault();
                                                focusNextInput(study.study_id, idx, studyAnalytes);
                                              }
                                            }}
                                          />
                                        )}
                                        {/* Espacio reservado para alinear con campos numéricos */}
                                        <div className="w-16 shrink-0"></div>
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-center">
                                        <Input
                                          placeholder="-"
                                          value={results[study.study_id]?.[analyte.id]?.unit || ""}
                                          onChange={(e) => handleResultChange(study.study_id, analyte.id, "unit", e.target.value)}
                                          className="h-9 w-20 text-center mx-auto"
                                        />
                                      </TableCell>
                                      <TableCell className="text-center text-muted-foreground">{formatReferenceDisplay(analyte.reference_text) || "-"}</TableCell>
                                    </TableRow>
                                  );
                                }
                                
                                // Default: pending referenced_value type
                                const pendingValue = results[study.study_id]?.[analyte.id]?.value || "";
                                const isCalculated = results[study.study_id]?.[analyte.id]?.calculated;
                                const pendingStatus = pendingValue ? getResultStatus(pendingValue, reference) : null;
                                
                                return (
                                  <TableRow key={analyte.id} className="bg-amber-50/30">
                                    <TableCell className="bg-amber-50/30 font-medium border-r text-center">
                                      {analyte.name}
                                      {isCalculated && (
                                        <Badge variant="outline" className="ml-2 text-xs bg-blue-100 text-blue-700 border-blue-300">
                                          Calc.
                                        </Badge>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-center" style={{ width: '180px', minWidth: '180px' }}>
                                      <div className="flex items-center justify-center gap-2 w-full">
                                        <Input
                                          placeholder="Valor"
                                          value={pendingValue}
                                          onChange={(e) => handleResultChange(study.study_id, analyte.id, "value", e.target.value)}
                                          className={`h-8 w-52 text-center shrink-0 ${isCalculated ? "bg-blue-50 border-blue-200 font-semibold" : ""}`}
                                          data-study={study.study_id}
                                          data-analyte-index={idx}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              focusNextInput(study.study_id, idx, studyAnalytes);
                                            }
                                          }}
                                        />
                                        {/* Alerta al lado del resultado - ancho fijo */}
                                        <div className="w-16 shrink-0 text-center">
                                          {pendingStatus && (
                                            <Badge 
                                              variant="outline" 
                                              className={`text-xs px-1.5 py-0 whitespace-nowrap ${
                                                pendingStatus === "high" ? "bg-red-100 text-red-700 border-red-300" : 
                                                pendingStatus === "low" ? "bg-orange-100 text-orange-700 border-orange-300" : 
                                                "bg-green-100 text-green-700 border-green-300"
                                              }`}
                                            >
                                              {pendingStatus === "high" ? "↑ Alto" : pendingStatus === "low" ? "↓ Bajo" : "✓"}
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell className="text-center text-muted-foreground">{unit}</TableCell>
                                    <TableCell className="text-center text-muted-foreground">{formatReferenceDisplay(reference)}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                          </div>
                          
                          {/* Show pending count info */}
                          {(() => {
                            const pendingCount = studyAnalytes.filter(a => 
                              !existingResult.values.find(v => v.analyte_id === a.id) && 
                              a.result_type !== "subtitle"
                            ).length;
                            const capturedCount = existingResult.values.length;
                            
                            return pendingCount > 0 ? (
                              <Alert className="bg-amber-50 border-amber-200 mt-4">
                                <AlertCircle className="h-4 w-4 text-amber-600" />
                                <AlertDescription className="text-amber-800">
                                  <span className="font-semibold">{pendingCount} analito(s) pendiente(s)</span> de capturar.
                                  {capturedCount > 0 && ` ${capturedCount} ya capturado(s).`}
                                </AlertDescription>
                              </Alert>
                            ) : null;
                          })()}
                          
                          {/* Observations field */}
                          <div className="mt-4 pt-3 border-t">
                            <Label className="text-sm font-medium mb-2 block">Observaciones</Label>
                            <textarea
                              className="w-full p-2 border rounded-md text-sm min-h-[60px] resize-y"
                              placeholder="Agregar observaciones del estudio..."
                              value={studyNotes[study.study_id] || ""}
                              onChange={(e) => handleNotesChange(study.study_id, e.target.value)}
                              disabled={existingResult.validated}
                            />
                          </div>
                          
                          {/* Action buttons at bottom right */}
                          <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
                            {/* Save pending results button */}
                            {(() => {
                              const hasPendingResults = Object.keys(results[study.study_id] || {}).length > 0;
                              return hasPendingResults && !existingResult.validated ? (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                  onClick={() => savePendingResults(existingResult.id, study.study_id, study.study_name)}
                                  disabled={saving}
                                >
                                  <Save className="h-4 w-4 mr-1" />
                                  Guardar Pendientes
                                </Button>
                              ) : null;
                            })()}
                            
                            {existingResult.validated ? (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                onClick={() => invalidateResult(existingResult.id)}
                                data-testid={`invalidate-${study.study_id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Invalidar
                              </Button>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    if (isEditing(study.study_id)) {
                                      updateResults(existingResult.id, study.study_id, study.study_name);
                                    } else {
                                      startEditing(study.study_id, existingResult);
                                    }
                                  }}
                                  disabled={saving}
                                  data-testid={`save-${study.study_id}`}
                                >
                                  <Save className="h-4 w-4 mr-1" />
                                  {isEditing(study.study_id) ? "Guardar Cambios" : "Editar"}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="default"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => validateResult(existingResult.id)}
                                  data-testid={`validate-${study.study_id}`}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Validar
                                </Button>
                              </>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Render analytes in table format */}
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-muted/50">
                                  <TableHead className="bg-muted/50 w-[180px] font-semibold text-center">Analito</TableHead>
                                  <TableHead className="text-center w-[220px] font-semibold">Resultado</TableHead>
                                  <TableHead className="text-center w-[100px] font-semibold">Unidad</TableHead>
                                  <TableHead className="text-center w-[150px] font-semibold">Referencia</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {studyAnalytes.map((analyte, idx) => {
                                  const currentValue = results[study.study_id]?.[analyte.id]?.value || "";
                                  const resultType = analyte.result_type || "referenced_value";
                                  const reference = getPatientReference(
                                    analyte, 
                                    selectedOrder?.patient_birth_date, 
                                    selectedOrder?.patient_gender
                                  );
                                  const status = currentValue && resultType === "referenced_value" 
                                    ? getResultStatus(currentValue, reference) 
                                    : null;
                                  const isCalculated = results[study.study_id]?.[analyte.id]?.calculated;
                                  
                                  // Subtitle type - hide completely, only show in PDF print
                                  if (resultType === "subtitle") {
                                    return null;
                                  }

                                  // Document type
                                  if (resultType === "document") {
                                    return (
                                      <TableRow key={analyte.id}>
                                        <TableCell colSpan={4} className="p-4">
                                          <div className="space-y-2">
                                            <Label className="flex items-center gap-2">
                                              <FileText className="h-4 w-4 text-muted-foreground" />
                                              {analyte.name}
                                            </Label>
                                            <RichTextEditor
                                              content={currentValue || analyte.document_template || ""}
                                              onChange={(html) =>
                                                handleResultChange(study.study_id, analyte.id, "value", html)
                                              }
                                              placeholder="Escribe el resultado aquí..."
                                            />
                                          </div>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  }

                                  // Text type
                                  if (resultType === "text") {
                                    const textOptions = analyte.text_options 
                                      ? analyte.text_options.split(',').map(opt => opt.trim()).filter(Boolean)
                                      : [];
                                    const hasOptions = textOptions.length > 0;
                                    
                                    return (
                                      <TableRow key={analyte.id}>
                                        <TableCell 
                                          className="bg-white font-medium border-r text-center cursor-pointer hover:text-primary hover:underline"
                                          onClick={() => fetchAnalyteHistory(analyte.id, analyte.name)}
                                          title="Ver historial de resultados"
                                        >
                                          {analyte.name}
                                        </TableCell>
                                        <TableCell className="text-center" style={{ width: '220px', minWidth: '220px' }}>
                                          <div className="flex items-center justify-center gap-2 w-full">
                                          {hasOptions ? (
                                            <Select
                                              value={currentValue}
                                              onValueChange={(value) =>
                                                handleResultChange(study.study_id, analyte.id, "value", value)
                                              }
                                            >
                                              <SelectTrigger 
                                                className="w-52 h-9 shrink-0" 
                                                data-testid={`result-${analyte.id}`}
                                                data-study={study.study_id}
                                                data-new-analyte-index={idx}
                                              >
                                                <SelectValue placeholder="Seleccionar..." />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {textOptions.map((option, i) => (
                                                  <SelectItem key={i} value={option}>{option}</SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          ) : (
                                            <Input
                                              placeholder="Resultado..."
                                              value={currentValue}
                                              onChange={(e) =>
                                                handleResultChange(study.study_id, analyte.id, "value", e.target.value)
                                              }
                                              className="h-9 w-52 text-center shrink-0"
                                              data-testid={`result-${analyte.id}`}
                                              data-study={study.study_id}
                                              data-new-analyte-index={idx}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  e.preventDefault();
                                                  focusNextInput(study.study_id, idx, studyAnalytes, true);
                                                }
                                              }}
                                            />
                                          )}
                                          {/* Espacio reservado para alinear con campos numéricos */}
                                          <div className="w-16 shrink-0"></div>
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                          <Input
                                            placeholder="-"
                                            value={results[study.study_id]?.[analyte.id]?.unit || ""}
                                            onChange={(e) => handleResultChange(study.study_id, analyte.id, "unit", e.target.value)}
                                            className="h-9 w-20 text-center mx-auto"
                                          />
                                        </TableCell>
                                        <TableCell className="text-center text-muted-foreground">
                                          {formatReferenceDisplay(analyte.reference_text) || "-"}
                                        </TableCell>
                                      </TableRow>
                                    );
                                  }

                                  // Default: referenced_value type
                                  return (
                                    <TableRow key={analyte.id}>
                                      <TableCell 
                                        className="bg-white font-medium border-r text-center cursor-pointer hover:text-primary hover:underline"
                                        onClick={() => fetchAnalyteHistory(analyte.id, analyte.name)}
                                        title="Ver historial de resultados"
                                      >
                                        {analyte.name}
                                        {isCalculated && (
                                          <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0 bg-blue-50 text-blue-600 border-blue-200">
                                            Calc
                                          </Badge>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-center" style={{ width: '220px', minWidth: '220px' }}>
                                        <div className="flex items-center justify-center gap-2 w-full">
                                          <Input
                                            placeholder="Valor"
                                            value={currentValue}
                                            onChange={(e) =>
                                              handleResultChange(study.study_id, analyte.id, "value", e.target.value)
                                            }
                                            className={`h-9 w-52 text-center shrink-0 ${isCalculated ? "bg-blue-50 border-blue-200 font-semibold" : ""}`}
                                            data-study={study.study_id}
                                            data-new-analyte-index={idx}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter') {
                                                e.preventDefault();
                                                const found = focusNextInput(study.study_id, idx, studyAnalytes, true);
                                                if (!found) {
                                                  const saveBtn = document.querySelector(`[data-testid="save-${study.study_id}"]`);
                                                  if (saveBtn) saveBtn.focus();
                                                }
                                              }
                                            }}
                                            data-testid={`result-${analyte.id}`}
                                          />
                                          {/* Alerta al lado del resultado - ancho fijo */}
                                          <div className="w-16 shrink-0 text-center">
                                            {status && (
                                              <Badge 
                                                variant="outline" 
                                                className={`text-xs px-1.5 py-0 whitespace-nowrap ${
                                                  status === "high" ? "bg-red-100 text-red-700 border-red-300" : 
                                                  status === "low" ? "bg-orange-100 text-orange-700 border-orange-300" : 
                                                  "bg-green-100 text-green-700 border-green-300"
                                                }`}
                                              >
                                                {status === "high" ? "↑ Alto" : status === "low" ? "↓ Bajo" : "✓"}
                                              </Badge>
                                            )}
                                          </div>
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-center text-muted-foreground">
                                        {analyte.unit || "-"}
                                      </TableCell>
                                      <TableCell className="text-center text-muted-foreground">
                                        {formatReferenceDisplay(reference)}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                          
                          {/* Observations field */}
                          <div className="mt-4 pt-3 border-t">
                            <Label className="text-sm font-medium mb-2 block">Observaciones</Label>
                            <textarea
                              className="w-full p-2 border rounded-md text-sm min-h-[60px] resize-y"
                              placeholder="Agregar observaciones del estudio..."
                              value={studyNotes[study.study_id] || ""}
                              onChange={(e) => handleNotesChange(study.study_id, e.target.value)}
                            />
                          </div>
                          
                          {/* Save button at bottom right */}
                          <div className="flex justify-end mt-4 pt-3 border-t">
                            <Button
                              size="sm"
                              onClick={() => saveResults(study.study_id, study.study_name)}
                              disabled={saving}
                              data-testid={`save-${study.study_id}`}
                            >
                              <Save className="h-4 w-4 mr-1" />
                              Guardar
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* PDF Preview Dialog - Compatible con Android */}
      <Dialog open={showPdfPreview} onOpenChange={setShowPdfPreview}>
        <DialogContent className="max-w-4xl h-[90vh] sm:h-[85vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Resultados de Laboratorio
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full overflow-hidden px-2 pb-2">
            {pdfUrl && (
              <MobilePDFViewer 
                pdfUrl={pdfUrl}
                title="Resultados de Laboratorio"
                filename={`resultados-${selectedOrder?.order_number || 'orden'}.pdf`}
                onPrint={() => setShowPdfPreview(false)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Analyte History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Historial de {selectedAnalyteName}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {historyLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : !historyData || historyData.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No hay resultados anteriores</p>
                <p className="text-sm mt-1">Este paciente no tiene historial para este analito</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead className="text-center">Resultado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyData.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="text-sm">
                        {item.date ? format(new Date(item.date), "dd/MM/yyyy", { locale: es }) : "-"}
                      </TableCell>
                      <TableCell className={`text-center font-medium ${item.is_abnormal ? "text-red-600" : ""}`}>
                        {item.value}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
